import React from 'react';
import clsx from 'clsx';
import Image from 'next/image';
import { useRouter } from 'next/router';

import { useViewerCourseStatusQuery, useViewerSubscriptionQuery, Video } from '@edx/graphql/generated';
import { formatDurationInSeconds } from '@edx/helpers/datetime';
import { Link } from '@edx/components/link';
import { Body } from '@edx/components/typography';
import { Badge } from '@edx/components/badge';

type Props = {
  courseSlug: string;
  relatedVideos: Video[];
};

export const VideoRelatedList: React.FC<Props> = ({ courseSlug, relatedVideos }) => {
  const router = useRouter();
  const subscriptionQuery = useViewerSubscriptionQuery({ ssr: false });
  const { data } = useViewerCourseStatusQuery({ variables: { slug: courseSlug } });
  const courseVideos = data?.course?.videos;

  const videos = React.useMemo(() => {
    if (!courseVideos) return relatedVideos;

    return relatedVideos.map((relatedVideo, index) => {
      const viewerHasCompleted = courseVideos[index]?.id === relatedVideo.id && courseVideos[index]?.viewerHasCompleted;
      return { ...relatedVideo, viewerHasCompleted };
    });
  }, [courseVideos, relatedVideos]);

  return (
    <ol>
      {videos.map(
        ({ name, level, description, isPublic, imageUrl, duration, viewerHasCompleted, resourcePath }, index) => {
          const videoIndex = index + 1;
          const isFirstVideo = videoIndex === 1;
          const isLastVideo = relatedVideos.length === videoIndex;
          const isCurrentVideo = router.asPath === resourcePath;

          const showFreeBadge =
            isPublic && !subscriptionQuery.loading && !subscriptionQuery?.data?.viewer.subscription?.isValid;

          return (
            <li key={resourcePath} className="relative group flex space-x-1 lg:space-x-2">
              <div className="flex-shrink-0 relative flex items-center">
                <div className={clsx('absolute inset-0 flex justify-center', { 'items-end': isFirstVideo })}>
                  {isLastVideo ? (
                    <div className="border-l border-gray-600 border-opacity-50 h-1/2" />
                  ) : (
                    <div
                      className={clsx('border-l border-gray-600 border-opacity-50', isFirstVideo ? 'h-1/2' : 'h-full')}
                    />
                  )}
                </div>

                <div className="relative flex items-center justify-center w-6 lg:w-8 h-6 lg:h-8 rounded-full bg-gray-800 border border-gray-600 font-display text-white text-base lg:text-lg">
                  {videoIndex}
                </div>
              </div>

              <Link
                href={resourcePath}
                className={clsx('flex-grow flex space-x-2 p-2 hover:bg-opacity-20 hover:bg-gray-600', {
                  'bg-opacity-20 bg-gray-600': isCurrentVideo,
                })}
              >
                <div className="self-start relative bg-gray-800 w-1/3 flex-shrink-0">
                  <Image src={imageUrl as string} width={238} height={134} layout="responsive" />

                  <Body
                    tag="span"
                    size="small"
                    variant="semibold"
                    className="absolute z-10 right-0 bottom-0 px-2 py-1 bg-gray-800 text-gray-300 tracking-widest bg-opacity-50 group-hover:bg-opacity-0 transition-all duration-200 ease transform group-hover:translate-y-1 group-hover:text-transparent"
                  >
                    {formatDurationInSeconds(duration)}
                  </Body>

                  {/* Overlay */}
                  <div className="absolute inset-0 cursor-pointer flex flex-col items-center space-y-2 justify-center bg-gray-800 bg-opacity-40 group-hover:bg-opacity-0 transition duration-300" />
                </div>

                <div className="w-2/3">
                  <div className="space-y-1">
                    <Body tag="h4" className="text-gray-300">
                      {name}
                    </Body>

                    {viewerHasCompleted ? (
                      <Badge background="dark" className="bg-gray-800">
                        Completado
                      </Badge>
                    ) : (
                      showFreeBadge && <Badge className="text-xs font-medium">Gratis</Badge>
                    )}
                  </div>
                </div>
              </Link>
            </li>
          );
        }
      )}
    </ol>
  );
};
